import { safeJsonParse } from '../lib/safeJson'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ColumnInfo, ItemInfo } from '../api/client'
import { COLUMN_TYPES, isImplementedType, typeSpec } from './columnTypes'
import { countryName } from './countries'
import { formatNumberValue, readNumFormat } from './formatNumber'
import { CountryEditor, HourEditor, PhoneEditor } from './cells/inlineEditors'
import { BooleanCell, ColorPickerCell, RatingCell } from './cells/toggleCells'
import {
  DurationEditor,
  EmailEditor,
  LinkEditor,
  TagEditor,
  TimerangeEditor,
  WeekEditor,
  emailAddress,
  formatDuration,
  formatEmail,
  formatLink,
  linkUrl,
  formatRange,
  formatTags,
  formatWeek,
} from './cells/popupEditors'
import { FileEditor, formatSize, parseAttachments } from './cells/fileCell'
import { useSettle } from './cells/settle'
/**
 * M9.3-fix：Ctrl（⌘）+ 点击把 link / email 直接打开。
 * 普通点击仍然进入编辑，避免两种意图打架。
 */
function openExternal(type: string, raw: string): boolean {
  if (raw === '') return false
  if (type === 'link') {
    const u = linkUrl(raw)
    if (u === '') return false
    window.open(u, '_blank', 'noopener,noreferrer')
    return true
  }
  if (type === 'email') {
    const a = emailAddress(raw)
    if (a === '') return false
    window.location.href = 'mailto:' + a
    return true
  }
  return false
}

/** 鼠标悬停提示：链接/邮箱直接显示地址（不再显示 JSON），其余保持「点击编辑」 */
function cellTitle(type: string, raw: string): string {
  if (type === 'link') {
    const u = linkUrl(raw)
    return u === '' ? '点击编辑' : u
  }
  if (type === 'email') {
    const a = emailAddress(raw)
    return a === '' ? '点击编辑' : a
  }
  return '点击编辑'
}

/** 已实现类型的 id 列表（SUPPORTED_TYPES 用） */
const IMPLEMENTED_IDS = COLUMN_TYPES.filter((t) => t.implemented).map((t) => t.id)
/** M7：由 BoardPage 注册的处理函数，供状态/下拉浮层里的「编辑标签」调用 */
let editLabelsHandler: ((columnId: number) => void) | null = null

export function setEditLabelsHandler(fn: ((columnId: number) => void) | null) {
  editLabelsHandler = fn
}

// M9.3 遗留 #2：看板级标签池（tag 列的候选来源）。
// 采用与 setEditLabelsHandler 相同的模块级注册表模式，避免给 CellView 加一堆 props。
let tagPool: Record<string, string[]> = {}

export function setTagPool(pool: Record<string, string[]>) {
  tagPool = pool
}


/** 状态列的标签表与颜色表（服务端已把库里的 JSON 文本解析成对象） */
/**
 * 数字列的显示格式化（M9.1 增强）：整数部分加千分位，最多保留原始小数位数。
 * 存储值始终是纯十进制字符串（不含分隔符），只在这里做展示。
 */
/** 十进制输入的规范化：去掉千分位、负号仅首位、小数点仅一个 */
function sanitizeNumeric(raw: string): string {
  let t = raw.replace(/[^0-9.-]/g, '')
  const neg = t.startsWith('-')
  t = t.replace(/-/g, '')
  const dot = t.indexOf('.')
  if (dot >= 0) t = t.slice(0, dot + 1) + t.slice(dot + 1).replace(/\./g, '')
  return (neg ? '-' : '') + t
}

function formatNumeric(raw: string): string {
  const t = raw.trim()
  if (t === '') return ''
  if (!/^-?\d+(\.\d+)?$/.test(t)) return t
  const neg = t.startsWith('-')
  const body = neg ? t.slice(1) : t
  const parts = body.split('.')
  const intPart = parts[0] ?? ''
  const decPart = parts[1]
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return (neg ? '-' : '') + grouped + (decPart === undefined ? '' : '.' + decPart)
}


export function labelTable(column: ColumnInfo): {
  text: Record<string, string>
  color: Record<string, string>
  keys: string[]
} {
  const text = (column.labels as { labels?: Record<string, string> } | null)?.labels ?? {}
  const color = (column.settings as { color_mapping?: Record<string, string> } | null)?.color_mapping ?? {}
  const keys = Object.keys(text).sort((a, b) => Number(a) - Number(b))
  return { text, color, keys }
}

/**
 * M3 支持的列类型（对齐 MONDAY-CLONE-SPEC.md §13.1 的六类）。
 * 其余类型先只读展示 —— 未实现的不要假装能编辑。
 */
export const SUPPORTED_TYPES = new Set<string>(['name', ...IMPLEMENTED_IDS])

/** long-text 的上限，来自 monday 的计数器（0/2000） */
const LONG_TEXT_MAX = 2000

/**
 * M9.2：下拉列改为**多值**。
 * 存储：JSON 数组字符串，如 `["0","2"]`；空值为 `""`。
 * 兼容：旧数据是单个索引（如 `"1"`），parse 时当成单元素数组处理。
 */
export function dropdownIndices(raw: string): string[] {
  if (raw === '') return []
  if (raw.startsWith('[')) {
    const arr = safeJsonParse<unknown>(raw, null, 'dropdownIndices')
    if (Array.isArray(arr)) return arr.map((x) => String(x))
  }
  return [raw]
}

export function isEditable(column: ColumnInfo): boolean {
  if (column.key === 'name') return true
  return isImplementedType(column.type)
}

/** 取单元格当前显示的原始值（名称列不在 values 里） */
export function rawValue(column: ColumnInfo, item: ItemInfo): string {
  if (column.key === 'name' || column.type === 'name') return item.name
  return item.values[column.key] ?? ''
}


function TextEditor({
  initial,
  busy,
  onCommit,
  onCancel,
}: {
  initial: string
  busy: boolean
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLInputElement>(null)
  const s = useSettle(onCommit, onCancel)

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  return (
    <input
      ref={ref}
      className="cell-input"
      value={value}
      disabled={busy}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') s.commit(value)
        else if (e.key === 'Escape') s.cancel()
      }}
      onBlur={() => s.commit(value)}
    />
  )
}

/** 多行文本：monday 是格内 textarea + 右下角 0/2000 计数器（spec §5.3） */
function LongTextEditor({
  initial,
  busy,
  onCommit,
  onCancel,
}: {
  initial: string
  busy: boolean
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLTextAreaElement>(null)
  const s = useSettle(onCommit, onCancel)

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  return (
    <span className="cell-longtext">
      <textarea
        ref={ref}
        className="cell-textarea"
        value={value}
        maxLength={LONG_TEXT_MAX}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) s.commit(value)
          else if (e.key === 'Escape') s.cancel()
        }}
        onBlur={() => s.commit(value)}
      />
      <span className="cell-counter">
        {value.length}/{LONG_TEXT_MAX}
      </span>
    </span>
  )
}

/** 数字列：只接受数字与负号/小数点，提交前做一次过滤 */
function NumericEditor({
  initial,
  busy,
  onCommit,
  onCancel,
}: {
  initial: string
  busy: boolean
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLInputElement>(null)
  const s = useSettle(onCommit, onCancel)

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  return (
    <input
      ref={ref}
      className="cell-input"
      inputMode="decimal"
      value={value}
      disabled={busy}
      onChange={(e) => {
        setValue(sanitizeNumeric(e.target.value))
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') s.commit(value)
        else if (e.key === 'Escape') s.cancel()
      }}
      onBlur={() => s.commit(value)}
    />
  )
}

/** 日期列：用原生 date 输入，行为与 monday 的日历弹层等价（值统一存 YYYY-MM-DD） */
function DateEditor({
  initial,
  busy,
  onCommit,
  onCancel,
}: {
  initial: string
  busy: boolean
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLInputElement>(null)
  const s = useSettle(onCommit, onCancel)

  useEffect(() => {
    ref.current?.focus()
  }, [])

  return (
    <input
      ref={ref}
      type="date"
      className="cell-input"
      value={value}
      disabled={busy}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') s.commit(value)
        else if (e.key === 'Escape') s.cancel()
      }}
      onBlur={() => s.commit(value)}
    />
  )
}

/** 下拉列：与状态列共用 labels，但渲染成无色的列表。
 *  和状态列一样用 fixed 浮层 —— 内联展开会挤占单元格宽度（撑不满、打开还会缩）。 */
function DropdownEditor({
  column,
  current,
  busy,
  onCommit,
  onCancel,
}: {
  column: ColumnInfo
  current: string
  busy: boolean
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const { text, keys } = labelTable(column)
  // M9.2：多选状态。点选项只切换勾选，按「完成」才提交 JSON 数组。
  const [sel, setSel] = useState<string[]>(() => dropdownIndices(current))
  const anchor = useRef<HTMLSpanElement>(null)
  const [pos, setPos] = useState<{ x: number; y: number; w: number } | null>(null)

  useLayoutEffect(() => {
    const r = anchor.current?.getBoundingClientRect()
    if (r !== undefined) setPos({ x: r.left, y: r.bottom + 8, w: Math.max(r.width, 168) })
  }, [])

  const toggle = (k: string) =>
    setSel((prev) =>
      prev.includes(k)
        ? prev.filter((x) => x !== k)
        : [...prev, k].sort((a, b) => Number(a) - Number(b)),
    )
  const commitNow = () => onCommit(sel.length === 0 ? '' : JSON.stringify(sel))

  return (
    <span ref={anchor} className="cell-status">
      {/* 单元格里铺满整格 */}
      {current === '' ? (
        <span className="cell-dd-value">
          <EmptyState />
        </span>
      ) : (
        <span className="cell-dd-value">
          {dropdownIndices(current)
            .map((k) => text[k] ?? k)
            .join(', ')}
        </span>
      )}

      <div className="panel-backdrop" onClick={onCancel} />

      <div
        className="status-panel"
        style={{ left: pos?.x ?? 0, top: pos?.y ?? 0, minWidth: pos?.w ?? 168 }}
      >
        <div className="status-panel__arrow" />
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            className={'cell-option' + (sel.includes(k) ? ' cell-option--on' : '')}
            disabled={busy}
            aria-pressed={sel.includes(k)}
            aria-label={`${sel.includes(k) ? '取消' : '选择'}${text[k] ?? k}`}
            onClick={() => toggle(k)}
          >
            <span className="cell-option__mark">{sel.includes(k) ? '✓' : ''}</span>
            {text[k] ?? k}
          </button>
        ))}
        {keys.length === 0 && <span className="dim">该列没有选项</span>}
        <div className="status-panel__sep" />
        <div className="cell-dd-foot">
          <button
            type="button"
            className="status-panel__action"
            disabled={busy}
            onClick={() => onCommit('')}
          >
            清除
          </button>
          <button
            type="button"
            className="status-panel__action status-panel__action--primary"
            disabled={busy}
            onClick={commitNow}
          >
            完成
          </button>
        </div>
          <button
            type="button"
            className="status-panel__action"
            title="编辑该列的标签与颜色"
            onClick={(e) => {
              e.stopPropagation()
              editLabelsHandler?.(column.id)
            }}
          >
            <span className="status-panel__icon">✎</span> 编辑标签
          </button>
      </div>
    </span>
  )
}

/** 状态列：按 monday 的样子做「锚在单元格下方的竖排色条浮层」
 *  —— 浮层用 position:fixed，因为单元格在横滚容器里，absolute 会被裁掉。 */
function ColorEditor({
  column,
  current,
  busy,
  onCommit,
  onCancel,
}: {
  column: ColumnInfo
  current: string
  busy: boolean
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const { text, color, keys } = labelTable(column)
  const anchor = useRef<HTMLSpanElement>(null)
  const [pos, setPos] = useState<{ x: number; y: number; w: number } | null>(null)

  useLayoutEffect(() => {
    const r = anchor.current?.getBoundingClientRect()
    if (r !== undefined) setPos({ x: r.left, y: r.bottom + 8, w: Math.max(r.width, 168) })
  }, [])

  const currentText = text[current]

  return (
    <span ref={anchor} className="cell-status">
      {currentText === undefined ? (
        <EmptyState />
      ) : (
        <span className="chip chip--cell" style={{ background: color[current] ?? '#c4c4c4' }}>
          {currentText}
        </span>
      )}

      <div className="panel-backdrop" onClick={onCancel} />

      <div
        className="status-panel"
        style={{ left: pos?.x ?? 0, top: pos?.y ?? 0, minWidth: pos?.w ?? 168 }}
      >
        <div className="status-panel__arrow" />
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            className={'status-bar' + (k === current ? ' status-bar--on' : '')}
            style={{ background: color[k] ?? '#c4c4c4' }}
            disabled={busy}
            aria-label={`设为${text[k] ?? k}`}
            title={`设为${text[k] ?? k}`}
            onClick={() => onCommit(k)}
          >
            {text[k] ?? k}
          </button>
        ))}
        {keys.length === 0 && <span className="dim">该列没有标签</span>}

        <div className="status-panel__sep" />
          <button
            type="button"
            className="status-panel__action"
            title="编辑该列的标签与颜色"
            onClick={(e) => {
              e.stopPropagation()
              editLabelsHandler?.(column.id)
            }}
          >
            <span className="status-panel__icon">✎</span> 编辑标签
          </button>

      </div>
    </span>
  )
}

/** 统一空态：`+` 图标 + aria-label="Empty cell"（spec §6），悬停才显现 */
function EmptyState() {
  return (
    <span className="cell-empty" role="img" aria-label="Empty cell" title="Empty cell">
      +
    </span>
  )
}

export function CellView({
  column,
  item,
  editing,
  busy,
  onStartEdit,
  onCommit,
  onCancel,
}: {
  column: ColumnInfo
  item: ItemInfo
  editing: boolean
  busy: boolean
  onStartEdit: () => void
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const editable = isEditable(column)
  const value = rawValue(column, item)

  const spec = typeSpec(column.type)

  // M9.0：注册表里有、但编辑器还没实现（或压根不认识）的类型 —— 不给编辑入口，
  // 否则用户能在语义不存在的列里写值，等于制造脏数据。
  // M9.6：公式列有真正的渲染（不再走"未实现"占位），错误态用红字
  if (column.type === 'formula') {
    if (value === '') return <EmptyState />
    const bad = value.startsWith('#') && value.endsWith('!')
    // backlog #7：按列设置显示格式（千分位 / 百分比 / 小数位）
    const shown = bad ? value : formatNumberValue(value, readNumFormat(column.settings))
    return (
      <span
        className={bad ? 'cell--err' : 'cell-formula'}
        title={bad ? `公式结果：${value}` : shown !== value ? `原值：${value}` : undefined}
      >
        {shown}
      </span>
    )
  }

  if (column.key !== 'name' && !editable) {
    const label = spec === undefined ? `未知类型 ${column.type}` : `${spec.label}（${column.type}）`
    return (
      <span className="cell-readonly cell-readonly--todo" title={`${label} 的编辑器尚未实现`}>
        {value === '' ? <EmptyState /> : <span>{value}</span>}
      </span>
    )
  }

  // M9.5：类D 只读派生列（服务端算好值，前端只展示，不进入编辑）
  const isDerivedRO =
    column.type === 'pulse-id' ||
    column.type === 'pulse-log' ||
    column.type === 'pulse-updated' ||
    column.type === 'autonumber' ||
    column.type === 'columns-battery' ||
    column.type === 'formula'

  // M9.2：直接操纵类型 —— 点单元格本身就是动作，不进入 editing 状态
  if (column.key !== 'name' && editable) {
    if (column.type === 'boolean') {
      return <BooleanCell value={value} busy={busy} onCommit={onCommit} />
    }
    if (column.type === 'rating') {
      return <RatingCell value={value} busy={busy} onCommit={onCommit} />
    }
    if (column.type === 'color-picker') {
      return <ColorPickerCell value={value} busy={busy} onCommit={onCommit} />
    }
  }

  if (editing && !isDerivedRO) {
    switch (column.type) {
      case 'color':
        return <ColorEditor column={column} current={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'dropdown':
        return <DropdownEditor column={column} current={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'long-text':
        return <LongTextEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'numeric':
        return <NumericEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'date':
        return <DateEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'email':
        return <EmailEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'link':
        return <LinkEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'timerange':
        return <TimerangeEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'tag':
        return <TagEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} suggestions={tagPool[column.key] ?? []} />
      case 'file':
        return (
          <FileEditor
            initial={value}
            busy={busy}
            onCommit={onCommit}
            onCancel={onCancel}
            itemId={item.id}
            columnKey={column.key}
          />
        )
      case 'duration':
        return <DurationEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'week':
        return <WeekEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'phone':
        return <PhoneEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'hour':
        return <HourEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      case 'country':
        return <CountryEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
      default:
        return <TextEditor initial={value} busy={busy} onCommit={onCommit} onCancel={onCancel} />
    }
  }

  // ---- 只读展示 ----
  if (column.type === 'columns-battery') {
    const pct = Number(value)
    const safe = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0
    return (
      <span className="battery" title={value === '' ? '未设置进度' : `${safe}%`}>
        <span className="battery__track">
          <span className="battery__fill" style={{ width: `${safe}%` }} />
        </span>
        <span className="battery__num">{value === '' ? '—' : `${safe}%`}</span>
      </span>
    )
  }

  let content: React.ReactNode
  // M9.6：公式错误态（#REF! / #DIV/0! / #CYCLE! / #ERROR!）红字显示
  if (value.startsWith('#') && value.endsWith('!')) {
    content = <span className="cell--err">{value}</span>
  } else if (value === '') {
    content = <EmptyState />
  } else if (column.type === 'color') {
    const { text, color } = labelTable(column)
    content =
      text[value] === undefined ? (
        <EmptyState />
      ) : (
        <span className="chip chip--cell" style={{ background: color[value] ?? '#c4c4c4' }}>
          {text[value]}
        </span>
      )
  } else if (column.type === 'dropdown') {
    const { text } = labelTable(column)
    const picked = dropdownIndices(value)
    content =
      picked.length > 1 ? (
        <span className="cell-dd-chips">
          {picked.map((k) => (
            <span key={k} className="cell-dd-chip">
              {text[k] ?? k}
            </span>
          ))}
        </span>
      ) : (
        <span>{text[picked[0] ?? ''] ?? picked[0] ?? ''}</span>
      )
    } else if (column.type === 'email') {
      content = <span>{formatEmail(value)}</span>
    } else if (column.type === 'link') {
      // 显示名或 URL，样式上做成链接色（点击仍是进入编辑）
      content = <span className="cell-link">{formatLink(value)}</span>
    } else if (column.type === 'timerange') {
      content = <span className="cell-pill">{formatRange(value)}</span>
    } else if (column.type === 'tag') {
      const tags = formatTags(value)
      content = (
        <span className="cell-tags">
          {tags.map((t) => (
            <span key={t} className="cell-tag">
              {t}
            </span>
          ))}
        </span>
      )
    } else if (column.type === 'file') {
      const files = parseAttachments(value)
      content = (
        <span className="cell-files">
          {files.map((f) => (
            <span key={f.id} className="cell-file" title={f.name + '（' + formatSize(f.size) + '）'}>
              {f.name}
            </span>
          ))}
        </span>
      )
    } else if (column.type === 'duration') {
      content = <span className="cell-duration">{formatDuration(value)}</span>
    } else if (column.type === 'week') {
      content = <span>{formatWeek(value)}</span>
    } else if (column.type === 'country') {
      content = <span>{countryName(value)}</span>
    } else if (column.type === 'numeric') {
      // 数字列：显示时加千分位，存储仍是纯十进制字符串
      content = <span>{formatNumeric(value)}</span>
    } else {
      content = <span>{value}</span>
  }

  if (!editable) {
    return (
      <span className="cell-readonly" title={`该列类型（${column.type}）暂不支持编辑`}>
        {content}
      </span>
    )
  }

  return (
    <button
      type="button"
      className={
        'cell-hit' +
        (column.type === 'color' ? ' cell-hit--fill' : '') +
        (column.type === 'link' || column.type === 'email' ? ' cell-hit--openable' : '')
      }
      onClick={(e) => {
        // M9.3-fix：链接/邮箱支持 Ctrl（⌘）+ 点击直接打开；普通点击仍是进入编辑
        if ((e.ctrlKey || e.metaKey) && openExternal(column.type, value)) return
        if (isDerivedRO) return   // 只读派生列：点击不进入编辑
        onStartEdit()
      }}
      disabled={busy}
      title={cellTitle(column.type, value)}
    >
      {content}
    </button>
  )
}

